<script setup>
import Icon from '@components/Icon.vue'
import ListingCard from '@components/ListingCard.vue'
import SectionContainer from '@components/SectionContainer.vue'
import CollectionProductLine from '@components/CollectionProductLine.vue'
import * as JsSearch from 'js-search'
import {ref, computed, onMounted, nextTick, watch} from 'vue'
import { groupBy } from '@helpers/utils'
import debounce from 'debounce'

const props = defineProps({
  collection: {
    type: Object,
    required: true,
  },
  enableSearch: {
    type: Boolean,
    default: false,
  },
})

const query = ref('')
const searchInput = ref('')
const sortBy = ref(props.collection.sort_by || 'rating_desc')
const isFilterOpen = ref(false)
const selectedTypes = ref([])
const selectedPotencies = ref([])
const selectedActiveIngredients = ref([])
const selectedDeals = ref([])

const numberOfProductLines = [...new Set(
  props.collection.listings.map(({ product_line }) => product_line?.id).filter(Boolean)
)].length

const potencyChoices = [...new Set(
  props.collection.listings.map(({ totalPotencyPerPiece }) => totalPotencyPerPiece).filter(Boolean)
)].sort((a, b) =>  Number(a) - Number(b)).map(p => p + 'mg')

const activeIngredientChoices = [...new Set(
  props.collection.listings.flatMap(({ active_ingredients }) => active_ingredients).filter(Boolean).map(i => i.name)
)]

const types = ['Indica', 'Sativa', 'Hybrid']
const typeChoices = [...new Set(
    props.collection.listings.flatMap(({ tags }) => tags)
)].filter(t => types.includes(t))

const dealChoices = ['Sales & Bundles Only', 'Exclude Bundles']

const activeFilters = computed(() => {
  return [...selectedActiveIngredients.value, ...selectedPotencies.value, ...selectedTypes.value, ...selectedDeals.value]
})

const productLines = computed(() => {
  let listings = props.collection.listings

  if (props.enableSearch && query.value) {
    const jsSearch = new JsSearch.Search('id')
    jsSearch.addIndex('title')
    jsSearch.addIndex('excerpt')
    jsSearch.addIndex('description')
    jsSearch.addDocuments(listings)
    listings = jsSearch.search(query.value)
  }

  if (selectedDeals.value.includes('Sales & Bundles Only')) {
    listings = listings.filter(
        listing => !!listing?.products?.[0]?.compare_at_price
    )
  }
  if (selectedDeals.value.includes('Exclude Bundles')) {
    listings = listings.filter(
        listing => listing?.products?.[0]?.quantity <= 1
    )
  }

  if (selectedTypes.value.length) {
    listings = listings.filter(
        ({tags}) => tags.some(i => selectedTypes.value.includes(i))
    )
  }

  if (selectedPotencies.value.length) {
    listings = listings.filter(
        ({totalPotencyPerPiece}) => selectedPotencies.value.includes(totalPotencyPerPiece + 'mg')
    )
  }

  if (selectedActiveIngredients.value.length) {
    listings = listings.filter(
        ({active_ingredients}) => active_ingredients.map(({name}) => name).some(i => selectedActiveIngredients.value.includes(i))
    )
  }

  switch (sortBy.value) {
    case 'rating_desc':
      listings = listings.sort((a, b) =>  Math.round(b.rating) - Math.round(a.rating) || b.total_reviews - a.total_reviews)
      break
    case 'manual':
      listings = listings.sort((a, b) => a.position - b.position)
      break
    case 'price_asc':
      listings = listings.sort((a, b) => parseFloat(a.products?.[0]?.price) - parseFloat(b.products?.[0]?.price))
      break
    case 'price_desc':
      listings = listings.sort((a, b) => parseFloat(b.products?.[0]?.price) - parseFloat(a.products?.[0]?.price))
      break
  }

  return props.collection.format === 'product_lines'
    ? groupBy(listings, listing => listing?.product_line?.title)
    : { listings }
})

const search = debounce(e => {
  query.value = e.target.value
}, 300)

function removeFilter(filter) {
  let index

  [selectedPotencies, selectedActiveIngredients, selectedTypes, selectedDeals].forEach(o => {
    index = o.value.indexOf(filter)
    if (index > -1) {
      o.value.splice(index, 1)
    }
  })
}

function trigger(name, value) {
  window.dataLayer.push({
    event: 'collection_filtered',
    filter_name: name,
    filter_value: value,
  })
}

watch(query, () => {
  if (searchInput.value) {
    searchInput.value.value = query.value
  }
})

onMounted(() => {
  nextTick(() => {
    if (props.enableSearch) {
      const params = new URLSearchParams(window.location.search)
      query.value = params.get('query') || ''
    }
  })

  window.criteo_q = window?.criteo_q || [];
  window.criteo_q.push(
      { event: "setAccount", account: 94147 },
      { event: "setSiteType", type: /iPad/.test(navigator.userAgent) ? "t" : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(navigator.userAgent) ? "m" : "d"},
      { event: "viewList",
        item: props.collection.listings.map(({ id }) => id),
        category: props.collection.id
      }
  );
})
</script>

<template>
  <div class="collection-filters" :class="{ 'collection-filters--open': isFilterOpen }">
    <div class="collection-filters__overlay" @click="isFilterOpen = false"></div>

    <div class="collection-filters__inner">
      <a href="#" @click.prevent="isFilterOpen = false" class="collection-filters__close">
        <Icon type="close-circle" />
      </a>

      <div class="collection-filters__title">FILTER</div>

      <div class="collection-filters__filters">
        <div v-if="activeIngredientChoices.length > 1">
          <div class="collection-filters__name">By Ingredient</div>
          <div class="collection-filters__options">
            <div class="collection-filters__option" v-for="activeIngredient in activeIngredientChoices" :key="activeIngredient">
              <input
                  type="checkbox"
                  :value="activeIngredient"
                  v-model="selectedActiveIngredients"
                  :id="`active-ingredient-${activeIngredient}`"
                  @change="trigger('Active Ingredients', activeIngredient)"
              />
              <label :for="`active-ingredient-${activeIngredient}`">{{ activeIngredient }}</label>
            </div>
          </div>
        </div>
        <div v-if="potencyChoices.length > 1">
          <div class="collection-filters__name">By Potency</div>
          <div class="collection-filters__options">
            <div class="collection-filters__option" v-for="potency in potencyChoices" :key="potency">
              <input
                  type="checkbox"
                  :value="potency"
                  v-model="selectedPotencies"
                  :id="`potency-${potency}`"
                  @change="trigger('Potency', potency)"
              />
              <label :for="`potency-${potency}`">{{ potency }}</label>
            </div>
          </div>
        </div>
        <div v-if="typeChoices.length > 1">
          <div class="collection-filters__name">By Type</div>
          <div class="collection-filters__options">
            <div class="collection-filters__option" v-for="type in typeChoices" :key="type">
              <input
                  type="checkbox"
                  :value="type"
                  v-model="selectedTypes"
                  :id="`type-${type}`"
                  @change="trigger('Type', type)"
              />
              <label :for="`type-${type}`">{{ type }}</label>
            </div>
          </div>
        </div>
        <div>
          <div class="collection-filters__name">By Best Deal</div>
          <div class="collection-filters__options">
            <div class="collection-filters__option" v-for="deal in dealChoices" :key="deal">
              <input
                  type="checkbox"
                  :value="deal"
                  v-model="selectedDeals"
                  :id="deal"
                  @change="trigger('Deal', deal)"
              />
              <label :for="deal">{{ deal }}</label>
            </div>
          </div>
        </div>
      </div>

      <button class="collection-filters__apply" type="button" @click="isFilterOpen = false">APPLY FILTER</button>
    </div>
  </div>

  <SectionContainer class="collection__section">
    <div class="collection__filters">
      <div class="collection__breadcrumbs">
        <slot />
      </div>

      <template v-if="collection.listings.length">
        <button type="button" class="collection__filters-btn" @click="isFilterOpen = true">
          <span>Filters</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
            <g clip-path="url(#clip0_5700_48937)">
              <path d="M17.9762 3.618C17.8919 3.32315 17.6572 3.14565 17.3323 3.13963C17.1037 3.13662 16.878 3.13963 16.6494 3.13963C16.6373 3.08548 16.6283 3.04637 16.6163 3.00725C16.2793 1.80383 15.5151 1.02461 14.3117 0.678621C14.1312 0.627475 13.9386 0.606415 13.7521 0.570312H13.2557C13.1414 0.588364 13.0271 0.600398 12.9127 0.624467C11.6672 0.895238 10.8368 1.62933 10.4276 2.83577C10.3946 2.93505 10.3705 3.03433 10.3404 3.13963H10.2231C7.64773 3.13963 5.07239 3.13963 2.49405 3.13963C1.87127 3.13963 1.2485 3.13662 0.622715 3.13963C0.270712 3.14565 -5.94081e-05 3.43146 0.00595773 3.78647C0.00595773 4.1475 0.276729 4.41827 0.640766 4.42128C0.986752 4.42128 1.33274 4.42128 1.67571 4.42128C4.33529 4.42128 6.99186 4.42128 9.65144 4.42128H10.3434C10.5931 5.21855 10.7165 5.46225 11.0745 5.88646C12.0162 6.99662 13.6649 7.32455 14.9525 6.64763C15.816 6.19634 16.3756 5.49534 16.6163 4.54463C16.6403 4.45137 16.6704 4.40925 16.7787 4.41527C16.9773 4.4273 17.1759 4.41827 17.3744 4.41527C17.7866 4.41226 18.0845 4.01212 17.9731 3.61198L17.9762 3.618ZM13.4362 5.71196C12.4253 5.68789 11.5709 4.82142 11.5739 3.78046C11.5739 2.71542 12.4404 1.85196 13.4994 1.85497C14.5674 1.85798 15.4309 2.71843 15.4279 3.77444C15.4399 4.84248 14.5434 5.73603 13.4362 5.71196Z" fill="#63605C"/>
              <path d="M17.964 10.0059C17.8708 9.72607 17.6271 9.56361 17.2901 9.56361C16.1077 9.56361 14.9254 9.56361 13.743 9.56361C11.9589 9.56361 10.1748 9.56361 8.39075 9.56361H7.65365C7.31669 8.01119 5.97486 7.00632 4.5488 6.99429C3.8809 6.98827 3.26715 7.16578 2.71357 7.53884C1.99151 8.02924 1.54324 8.70617 1.34768 9.56361C1.11903 9.56361 0.893386 9.56361 0.667743 9.56361C0.198406 9.56963 -0.108468 9.98782 0.0359432 10.418C0.123192 10.6828 0.363877 10.8513 0.667743 10.8543C0.893386 10.8543 1.11602 10.8543 1.35069 10.8543C1.36272 10.9084 1.37476 10.9476 1.38378 10.9867C1.81401 12.7768 3.73949 13.8568 5.48747 13.2672C6.61268 12.8881 7.3227 12.1028 7.62657 10.9536C7.64462 10.8904 7.65665 10.8543 7.74089 10.8543C9.93113 10.8573 12.1184 10.8543 14.3086 10.8543C15.3105 10.8543 16.3153 10.8543 17.3172 10.8543C17.7955 10.8543 18.1144 10.4361 17.97 10.0059H17.964ZM4.5127 12.142C3.43563 12.142 2.56916 11.2785 2.57217 10.2074C2.57217 9.13639 3.44165 8.27895 4.51871 8.27895C5.5657 8.27895 6.42916 9.15444 6.42615 10.2135C6.42615 11.2695 5.55968 12.142 4.5127 12.142Z" fill="#63605C"/>
            </g>
            <defs>
              <clipPath id="clip0_5700_48937">
                <rect width="18.0003" height="12.8646" fill="white" transform="translate(0 0.568359)"/>
              </clipPath>
            </defs>
          </svg>
        </button>

        <div class="collection__sort">
          <select v-model="sortBy">
            <option value="rating_desc">Highest Rated</option>
            <option value="manual">Relevance</option>
            <option value="price_asc">Price (Low to High)</option>
            <option value="price_desc">Price (High to Low)</option>
          </select>
          <Icon type="caret" class="collection__sort-caret" />
        </div>

        <div class="collection__search" v-if="enableSearch">
          <div class="search">
            <input ref="searchInput" @input="search" placeholder="Search Products" />
            <Icon type="search" class="search__icon" />
          </div>
        </div>
      </template>
    </div>

    <div v-if="activeFilters.length" class="collection__active-filters">
      <span class="collection__active-filter" v-for="filter in activeFilters" :key="filter" @click="removeFilter(filter)">
        <span>{{ filter }}</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
          <rect x="10.0664" y="0.511719" width="1.29404" height="14.2344" rx="0.64702" transform="rotate(45 10.0664 0.511719)" fill="white"/>
          <rect x="10.9805" y="10.5742" width="1.29404" height="14.2344" rx="0.647021" transform="rotate(135 10.9805 10.5742)" fill="white"/>
        </svg>
      </span>
    </div>

    <div class="collection__lines">
      <div class="collection__empty-lines" v-if="collection.listings.length === 0">
        No products available.
      </div>
      <div class="collection__empty-lines" v-else-if="!Object.values(productLines)?.[0]?.length && (activeFilters.length > 0 || !!query)">
        No products match your search.
      </div>
      <template v-else-if="props.collection.format === 'product_lines' && numberOfProductLines > 1">
        <div v-for="(listings, productLine) in productLines" :key="productLine">
          <CollectionProductLine :listings="listings" />
        </div>
      </template>
      <div class="collection__no-lines" v-else>
        <div v-for="listing in Object.values(productLines)[0]" :key="listing.id">
          <slot name="listing" :listing="listing">
            <ListingCard :listing="listing" />
          </slot>
        </div>
      </div>
    </div>
  </SectionContainer>
</template>

<style lang="scss" scoped>
.collection__filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .collection__breadcrumbs {
    flex: 1 0 100%;

    @media screen and (min-width: 768px) {
      order: 1;
      flex: 1;
    }
  }

  .collection__filters-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;

    @media screen and (min-width: 768px) {
      order: 3;
    }
  }

  .collection__sort select,
  .collection__filters-btn,
  .collection__search input {
    border-radius: 4px;
    border: 0;
    background: var(--Neutral-50, #F5F5F5);
    color: var(--Neutral-600, #8A8885);
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px; /* 142.857% */
    padding: 8px 16px;
  }

  .collection__sort {
    position: relative;
    flex: 1;

    @media screen and (min-width: 768px) {
      order: 4;
      flex: initial;
    }

    select {
      display: block;
      width: 100%;
      padding: 8px 32px 8px 16px;
      -webkit-appearance: none;
      -moz-appearance: none;
      cursor: pointer;
    }

    :deep(svg) {
      position: absolute;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);

      path {
        stroke: var(--neutral-1000, #3C3833);
      }
    }
  }

  .collection__search {
    flex: 1 0 100%;

    @media screen and (min-width: 768px) {
      order: 2;
      flex: initial;
    }

    .search {
      position: relative;

      input {
        display: block;
        width: 100%;
      }

      .search__icon {
        position: absolute;
        right: 12px;
        top: 50%;
        transform: translateY(-50%);
        height: 16px;
        width: 16px;

        @media screen and (min-width: 768px) {
          right: 16px;
        }

        :deep(path) {
          fill: var(--neutral-700, #767370);
        }
      }
    }
  }
}

.collection__active-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;

  @media screen and (min-width: 768px) {
    justify-content: flex-end;
  }

  .collection__active-filter {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    color: var(--Neutral-50, #F5F5F5);
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 20px; /* 142.857% */
    border-radius: 32px;
    background: var(--Primary-600, #183D4F);
    padding: 4px 10px;
  }
}

.collection-filters {
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  height: 100%;
  justify-content: flex-start;
  left: 0;
  position: fixed;
  top: 0;
  transition: visibility var(--duration-default) ease;
  width: 100vw;
  z-index: 2147483001;
  visibility: hidden;

  &.collection-filters--open {
    visibility: visible;
  }

  .collection-filters__overlay {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
  }

  &.collection-filters--open .collection-filters__inner {
    transform: translateX(0);
  }

  .collection-filters__close {
    position: absolute;
    top: 12px;
    right: 16px;
    background-color: #EBEBEA;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;

    @media screen and (min-width: 768px) {
      top: 12px;
      right: 20px;
      width: 32px;
      height: 32px;
    }
  }

  .collection-filters__apply {
    color: var(--Neutral-50, #F5F5F5);
    text-align: center;
    font-family: Outfit, 'Outfit Fallback', Arial, serif;
    font-size: 12px;
    font-weight: 700;
    line-height: 15px; /* 125% */
    border-radius: 21px;
    background: var(--Orange---terracotta, #DB7E5A);
    padding: 10px;
    border: 0;
    cursor: pointer;
  }

  .collection-filters__inner {
    max-width: calc(100vw - 3rem);
    transform: translateX(100%);
    transition: transform var(--duration-default) ease;
    width: 32rem;
    background: var(--shades-white, #FFF);
    box-shadow: 0 4px 6px -4px rgba(16, 24, 40, 0.10), 0px 10px 15px -3px rgba(16, 24, 40, 0.10);
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
    padding: 12px 16px 16px 16px;

    @media screen and (min-width: 768px) {
      padding: 12px 60px 16px 60px;
    }

    .collection-filters__title {
      color: var(--Neutral-500, #9D9B99);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 16px;
      font-weight: 700;
      line-height: 24px; /* 150% */
      margin-bottom: 14px;

      @media screen and (min-width: 768px) {
        font-size: 20px;
        margin-bottom: 28px;
      }
    }

    .collection-filters__filters {
      flex: 1;
    }

    .collection-filters__name {
      color: var(--Neutral-900, #4F4C47);
      font-family: Outfit, 'Outfit Fallback', Arial, serif;
      font-size: 12px;
      font-weight: 800;
      line-height: 20px; /* 166.667% */
      margin-bottom: 8px;

      @media screen and (min-width: 768px) {
        font-size: 16px;
        margin-bottom: 20px;
      }
    }

    .collection-filters__options {
      margin-bottom: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      @media screen and (min-width: 768px) {
        margin-bottom: 36px;
      }

      input {
        border: 0;
        clip: rect(0 0 0 0);
        height: 1px;
        margin: -1px;
        overflow: hidden;
        padding: 0;
        position: absolute;
        width: 1px;
      }

      label {
        color: var(--Neutral-700, #767370);
        text-align: center;
        font-family: Outfit, 'Outfit Fallback', Arial, serif;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px; /* 142.857% */
        border-radius: 32px;
        background: var(--Neutral-50, #F5F5F5);
        padding: 4px 10px;
        cursor: pointer;

        @media screen and (min-width: 768px) {
          font-size: 16px;
          padding: 4px 12px;
        }
      }

      input:checked + label {
        background: var(--Primary-600, #183D4F);
        color: var(--Neutral-50, #F5F5F5);
      }
    }
  }
}

.collection__section {
  padding: 8px 12px;

  @media screen and (min-width: 768px) {
    padding: 48px 0;
  }
}

.collection__lines {
  display: flex;
  flex-direction: column;
  gap: 48px;
  margin-top: 28px;

  @media screen and (min-width: 768px) {
    flex: 1 1 0;
    min-width: 0;
    margin-top: 40px;
    gap: 96px;
  }

  .collection__empty-lines {
    text-align: center;
  }

  .collection__no-lines {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;

    @media screen and (min-width: 768px) {
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 32px;
    }
  }
}
</style>
